import { useState, useEffect, useCallback } from 'react';
import { useAuth, type Project } from './auth';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// Global project selector. All pages read and write the same active project,
// which is also the key authFetch uses for the X-Project-Id header.
const PROJECT_FILTER_EVENT = 'bp:projectfilterchange';
const ACTIVE_PROJECT_KEY = 'active_project_id';
type ProjectFilterEventDetail = { id: string };

// Hook + co-located component. 30+ consumers use the hook; some also use
// the component. Splitting the hook into its own file is recommended for
// Fast Refresh but would touch every consumer's import path. Inline-disable
// keeps the surface stable.
// eslint-disable-next-line react-refresh/only-export-components
export function useProjectFilter(_pageKey?: string) {
  const { projects } = useAuth();

  const [projectId, setProjectIdState] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(ACTIVE_PROJECT_KEY) ?? '';
  });

  const setProjectId = useCallback((id: string) => {
    setProjectIdState(id);
    if (id) localStorage.setItem(ACTIVE_PROJECT_KEY, id);
    else localStorage.removeItem(ACTIVE_PROJECT_KEY);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<ProjectFilterEventDetail>(
        PROJECT_FILTER_EVENT,
        { detail: { id } },
      ));
    }
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ProjectFilterEventDetail>).detail;
      if (!detail) return;
      setProjectIdState((prev) => prev === detail.id ? prev : detail.id);
    };
    window.addEventListener(PROJECT_FILTER_EVENT, handler);
    return () => window.removeEventListener(PROJECT_FILTER_EVENT, handler);
  }, []);

  // Auto-select the first project the first time `projects` arrives if
  // nothing is saved. Deferred via queueMicrotask so the setState lands in
  // a callback rather than the effect body.
  useEffect(() => {
    const saved = localStorage.getItem(ACTIVE_PROJECT_KEY);
    if (saved || projects.length === 0) return;
    const first = projects[0];
    queueMicrotask(() => setProjectIdState(first.id));
    localStorage.setItem(ACTIVE_PROJECT_KEY, first.id);
  }, [projects]);

  useEffect(() => {
    if (projectId) localStorage.setItem(ACTIVE_PROJECT_KEY, projectId);
    else localStorage.removeItem(ACTIVE_PROJECT_KEY);
  }, [projectId]);

  const project: Project | null = projects.find((p) => p.id === projectId) ?? null;

  return { projectId, setProjectId, project, projects };
}

export function ProjectFilter({
  pageKey,
  className,
  allowAll = false,
  triggerClassName,
  global = false,
}: {
  pageKey?: string;
  className?: string;
  /** When false, hides the "All projects" option — the page requires a single project. */
  allowAll?: boolean;
  triggerClassName?: string;
  /** Only the sidebar renders the global selector; page-level selectors are retired. */
  global?: boolean;
}) {
  const { projectId, setProjectId, projects } = useProjectFilter(pageKey);
  if (!global) return null;
  return (
    <div className={className}>
      <Select
        value={projectId || (allowAll ? '__all' : '')}
        onValueChange={(v) => setProjectId(!v || v === '__all' ? '' : v)}
      >
        <SelectTrigger className={triggerClassName ?? 'h-8 w-48 text-xs'}>
          <SelectValue placeholder="Project" />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="__all">All projects</SelectItem>}
          {projects.map((p) => (
            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
